import chroma from "chroma-js";

// chroma-js exposes deltaE at runtime but omits it from its type definitions.
const chromaWithDeltaE = chroma as typeof chroma & { deltaE: (a: string, b: string) => number };

/**
 * Utilities for color manipulation and conversion.
 */

/**
 * Converts a Hex or RGB/RGBA color string to an RGBA string with the specified opacity.
 * 
 * @param color - The input color string (e.g., "#ff0000", "rgb(255, 0, 0)").
 * @param opacity - The desired opacity (0.0 to 1.0).
 * @returns An RGBA string (e.g., "rgba(255, 0, 0, 0.5)").
 */
export function withOpacity(color: string, opacity: number): string {
  if (!color) return `rgba(0, 0, 0, ${opacity})`;

  // Handle Hex
  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex.split("").map(c => c + c).join("");
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // Handle RGB/RGBA
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (match) {
    return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${opacity})`;
  }

  // Fallback: use chroma for named colors and other valid CSS formats
  try {
    return chroma(color).alpha(opacity).css();
  } catch {
    return color;
  }
}

/**
 * Computes Delta E (perceptual color difference) between two colors.
 * Uses the CIE 2000 standard. Values <2.3 are "Just Noticeable Difference".
 * 
 * @param color1 - First color string (hex, rgb, rgba, or named color).
 * @param color2 - Second color string (hex, rgb, rgba, or named color).
 * @returns Delta E value (0 = identical, higher = more different).
 */
function getDeltaE(color1: string, color2: string): number | null {
  // chroma.deltaE exists at runtime even though it's not in type definitions
  try {
    return chromaWithDeltaE.deltaE(color1, color2);
  } catch {
    return null;
  }
}

/**
 * Validates that all colors in a list have sufficient perceptual distance.
 * Uses Delta E 2000 (CIE standard) with a minimum threshold of 4.0.
 * 
 * @param colors - Array of color strings (hex, rgb, rgba, or named colors).
 * @param minDeltaE - Minimum acceptable Delta E distance (default: 4.0).
 * @returns Object with `valid` boolean and optional `conflicts` array detailing similar colors.
 * 
 * @example
 * const result = validateColorDistinctness(['#ff0000', '#ff0011']);
 * if (!result.valid) {
 *   console.log('Colors too similar:', result.conflicts);
 * }
 */
export function validateColorDistinctness(
  colors: string[],
  minDeltaE: number = 4.0
): { valid: boolean; conflicts?: Array<{ indices: [number, number]; deltaE: number }> } {
  if (colors.length < 2) return { valid: true };

  const conflicts: Array<{ indices: [number, number]; deltaE: number }> = [];

  for (let i = 0; i < colors.length; i++) {
    for (let j = i + 1; j < colors.length; j++) {
      const deltaE = getDeltaE(colors[i], colors[j]);
      if (deltaE === null) {
        return { valid: false, conflicts: [{ indices: [i, j], deltaE: 0 }] };
      }
      if (deltaE < minDeltaE) {
        conflicts.push({
          indices: [i, j],
          deltaE: Math.round(deltaE * 100) / 100,
        });
      }
    }
  }

  return {
    valid: conflicts.length === 0,
    conflicts: conflicts.length > 0 ? conflicts : undefined,
  };
}

/**
 * Finds a random color that is perceptually distinct from all provided colors.
 *
 * Generates random colors and checks each against the existing set using Delta E 2000.
 * If no fully-qualifying color is found within the attempt limit, returns the best
 * candidate seen (highest minimum Delta E against existing colors).
 *
 * @param existingColors - Colors the result must be distinct from.
 * @param minDeltaE - Required minimum Delta E distance from every existing color (default: 4.0).
 * @param maxAttempts - Maximum number of random colors to try before giving up (default: 200).
 * @returns A hex color string, or `null` if no candidate could be found at all.
 */
export function findDistinctColor(
  existingColors: string[],
  minDeltaE: number = 4.0,
  maxAttempts: number = 200
): string | null {
  // With no existing colors any random color is distinct
  if (existingColors.length === 0) return chroma.random().hex();

  let bestCandidate: string | null = null;
  let bestMinDeltaE = -Infinity;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const candidate = chroma.random().hex();

    // Find the minimum deltaE between this candidate and all existing colors
    let minDistance = Infinity;
    for (const existing of existingColors) {
      const d = getDeltaE(candidate, existing);
      if (d !== null && d < minDistance) minDistance = d;
    }

    if (minDistance >= minDeltaE) {
      return candidate;
    }

    // Track the best option in case we exhaust all attempts
    if (minDistance > bestMinDeltaE) {
      bestMinDeltaE = minDistance;
      bestCandidate = candidate;
    }
  }

  return bestCandidate;
}

/**
 * Finds the closest existing color to a given color.
 * Useful for suggesting a replacement when a color is too similar to others.
 * 
 * @param targetColor - The color to match against.
 * @param existingColors - Array of existing colors to compare against.
 * @returns Object with the index and Delta E distance of the closest color.
 */
export function findClosestColor(
  targetColor: string,
  existingColors: string[]
): { index: number; deltaE: number } | null {
  if (existingColors.length === 0) return null;

  try {
    // Validate targetColor can be parsed
    chroma(targetColor);
    
    let closest = { index: 0, deltaE: Infinity };

    existingColors.forEach((color, index) => {
      const distance = getDeltaE(targetColor, color);
      if (distance !== null && distance < closest.deltaE) {
        closest = { index, deltaE: Math.round(distance * 100) / 100 };
      }
    });

    return closest;
  } catch {
    return null;
  }
}
